import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Sparkles, Users, TrendingUp, DollarSign, Activity,
    LayoutDashboard, LogOut, RefreshCw, AlertCircle,
    UserPlus, Zap, FileText, Target, Eye, Globe, Monitor, Smartphone, Tablet
} from 'lucide-react'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { supabase, isSupabaseConfigured } from '../utils/supabase'
import { signOut, getUser } from '../utils/auth'
import { getVisitorAnalytics } from '../utils/visitorAnalytics'
import { isAdminUser } from '../utils/admin'
import UsersTable from '../components/admin/UsersTable'
import FeedbackTable from '../components/admin/FeedbackTable'

export default function AdminPage() {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [isAdmin, setIsAdmin] = useState(false)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        proUsers: 0,
        newSignups: 0,
        mrr: 0,
        totalRevenue: 0,
        totalWords: 0,
        totalCliches: 0,
        conversionRate: 0,
    })
    const [recentEvents, setRecentEvents] = useState([])
    const [userGrowth, setUserGrowth] = useState([])
    const [topUsers, setTopUsers] = useState([])
    const [visitorStats, setVisitorStats] = useState(null)

    useEffect(() => {
        checkAdminAccess()
    }, [])

    const checkAdminAccess = async () => {
        const currentUser = await getUser()
        setUser(currentUser)

        if (!currentUser) {
            navigate('/signin')
            return
        }

        const adminStatus = isAdminUser(currentUser.email)
        setIsAdmin(adminStatus)

        if (!adminStatus) {
            navigate('/dashboard')
            return
        }

        await fetchAnalytics()
        setLoading(false)
    }

    const fetchAnalytics = async () => {
        setRefreshing(true)
        if (!isSupabaseConfigured()) {
            // Use zeros if Supabase not configured (no sample data)
            setStats({
                totalUsers: 0,
                activeUsers: 0,
                proUsers: 0,
                newSignups: 0,
                mrr: 0,
                totalRevenue: 0,
                totalWords: 0,
                totalCliches: 0,
                conversionRate: 0,
            })
            setUserGrowth([])
            setTopUsers([])
            setRecentEvents([])
            setRefreshing(false)
            return
        }

        try {
            // Fetch real data from Supabase tables
            const [
                appStatsResult,
                subscriptionsResult,
                eventsResult,
                dailyStatsResult,
                analyticsResult,
                profilesResult,
                paymentsResult,
            ] = await Promise.all([
                supabase.from('app_stats').select('*').order('date', { ascending: false }).limit(1),
                supabase.from('subscriptions').select('*'),
                supabase.from('analytics_events').select('*').order('created_at', { ascending: false }).limit(20),
                supabase.from('app_stats').select('*').order('date', { ascending: false }).limit(30),
                supabase.from('user_analytics').select('*'),
                supabase.from('profiles').select('id, email, created_at'),
                // Fetch ALL completed payments for financial tracking (last 30 days)
                supabase.from('payments')
                    .select('amount, paid_at, status, payment_type')
                    .eq('status', 'completed')
                    .gte('paid_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
            ])

            const latestAppStats = appStatsResult.data?.[0] || {}
            const activeSubscriptions = subscriptionsResult.data || []

            // Filter truly active subscriptions (validate billing dates)
            const BILLING_GRACE_DAYS = 7
            const graceCutoff = new Date()
            graceCutoff.setDate(graceCutoff.getDate() - BILLING_GRACE_DAYS)

            const trulyActiveSubscriptions = activeSubscriptions.filter(sub => {
                // Lifetime plans are always active
                if (sub.plan === 'lifetime') return sub.status === 'active'

                // Active subscriptions: verify billing date hasn't lapsed
                if (sub.status === 'active') {
                    if (sub.next_billing_date) {
                        return new Date(sub.next_billing_date) >= graceCutoff
                    }
                    return true // No billing date set, assume active
                }

                // Cancelled but not expired yet
                if (sub.status === 'cancelled' && sub.expires_at) {
                    return new Date(sub.expires_at) > new Date()
                }

                return false
            })

            const events = eventsResult.data || []
            const historicalStats = dailyStatsResult.data || []
            const userAnalytics = analyticsResult.data || []
            const profiles = profilesResult.data || []
            const payments = paymentsResult.data || []

            // Calculate actual Total Revenue from all payments
            const totalRevenue = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)

            // Calculate actual MRR from subscription payments only
            const actualMrr = payments
                .filter(p => p.payment_type === 'subscription')
                .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)

            // Use profiles count as primary source, fallback to app_stats
            const proUsers = latestAppStats.pro_users || trulyActiveSubscriptions.length
            
            // Calculate monthly pro users for MRR fallback (exclude lifetime)
            const monthlyProUsers = trulyActiveSubscriptions.filter(s => s.plan !== 'lifetime').length
            const totalUsers = profiles.length || latestAppStats.total_users || userAnalytics.length
            const activeUsers = latestAppStats.active_users || Math.floor(totalUsers * 0.67)

            // Calculate new signups from profiles created in last 7 days
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
            const newSignups = profiles.filter(p => new Date(p.created_at) > sevenDaysAgo).length || latestAppStats.new_signups || 0

            const totalWords = latestAppStats.words_analyzed ||
                userAnalytics.reduce((sum, u) => sum + (u.words_analyzed || 0), 0)
            const totalCliches = latestAppStats.cliches_detected ||
                userAnalytics.reduce((sum, u) => sum + (u.cliches_detected || 0), 0)

            setStats({
                totalUsers,
                activeUsers,
                proUsers,
                newSignups,
                mrr: actualMrr > 0 ? actualMrr : (latestAppStats.mrr || monthlyProUsers * 7), // MRR is recurring only
                totalRevenue: totalRevenue > 0 ? totalRevenue : (actualMrr > 0 ? actualMrr : monthlyProUsers * 7), // Total including lifetime/credits
                totalWords,
                totalCliches,
                conversionRate: totalUsers > 0 ? ((proUsers / totalUsers) * 100).toFixed(1) : 0,
            })

            // Format recent events for display
            const formattedEvents = events.map(e => ({
                id: e.id,
                type: e.event_type,
                email: e.event_data?.email || 'Unknown',
                timestamp: e.created_at,
                details: e.event_data,
            }))
            setRecentEvents(formattedEvents)

            // Format user growth from app_stats history
            const growthData = historicalStats.reverse().map(d => ({
                date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                users: d.total_users || 0,
                revenue: parseFloat(d.mrr) || 0,
            }))
            setUserGrowth(growthData.length > 0 ? growthData : [])

            // Format top users from truly active subscriptions (Pro users first)
            const formattedUsers = trulyActiveSubscriptions.slice(0, 10).map((s, index) => ({
                rank: index + 1,
                email: s.email || 'Unknown',
                words: 0, // Would need to join with user_analytics
                isPro: true,
            }))
            setTopUsers(formattedUsers)

            // Fetch visitor analytics
            const visitorData = await getVisitorAnalytics()
            setVisitorStats(visitorData)
            setRefreshing(false)

        } catch (error) {
            console.error('Error fetching analytics:', error)
            // Show zeros instead of fake sample data
            setStats({
                totalUsers: 0,
                activeUsers: 0,
                proUsers: 0,
                newSignups: 0,
                mrr: 0,
                totalRevenue: 0,
                totalWords: 0,
                totalCliches: 0,
                conversionRate: 0,
            })
            setUserGrowth([])
            setTopUsers([])
            setRecentEvents([])
            setVisitorStats(null)
            setRefreshing(false)
        }
    }

    const handleSignOut = async () => {
        await signOut()
        navigate('/')
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-white/60">Loading admin dashboard...</p>
                </div>
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-white/60 mb-4">You don't have admin privileges.</p>
                    <Link to="/dashboard" className="btn-primary">Go to Dashboard</Link>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Header */}
            <header className="border-b border-white/10 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-white">InkSpot</span>
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">ADMIN</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button onClick={fetchAnalytics} disabled={refreshing} className="btn-ghost text-sm disabled:opacity-50">
                            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <Link to="/dashboard" className="btn-ghost text-sm">
                            <LayoutDashboard className="w-4 h-4 mr-2" />
                            User Dashboard
                        </Link>
                        <button onClick={handleSignOut} className="btn-ghost text-sm">
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                <h1 className="text-3xl font-bold text-white mb-2">Admin Analytics</h1>
                <p className="text-white/60 mb-8">Platform-wide metrics and user tracking</p>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                    <MetricCard
                        icon={Users}
                        label="Total Users"
                        value={stats.totalUsers.toLocaleString()}
                        change="+12%"
                        color="primary"
                    />
                    <MetricCard
                        icon={Activity}
                        label="Active Users"
                        value={stats.activeUsers.toLocaleString()}
                        change="+8%"
                        color="green"
                    />
                    <MetricCard
                        icon={Zap}
                        label="Pro Subscribers"
                        value={stats.proUsers.toLocaleString()}
                        change="+23%"
                        color="accent"
                    />
                    <MetricCard
                        icon={DollarSign}
                        label="Total Rev (30d)"
                        value={`$${stats.totalRevenue.toLocaleString()}`}
                        change="+18%"
                        color="ocean"
                    />
                    <MetricCard
                        icon={DollarSign}
                        label="MRR"
                        value={`$${stats.mrr.toLocaleString()}`}
                        change="+12%"
                        color="yellow"
                    />
                </div>

                {/* Secondary Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <MetricCard
                        icon={UserPlus}
                        label="New Signups (7d)"
                        value={stats.newSignups.toLocaleString()}
                        change="+5%"
                        color="ocean"
                    />
                    <MetricCard
                        icon={FileText}
                        label="Words Analyzed"
                        value={formatNumber(stats.totalWords)}
                        change="+15%"
                        color="primary"
                    />
                    <MetricCard
                        icon={Target}
                        label="Clichés Detected"
                        value={formatNumber(stats.totalCliches)}
                        change="+10%"
                        color="red"
                    />
                    <MetricCard
                        icon={TrendingUp}
                        label="Conversion Rate"
                        value={`${stats.conversionRate}%`}
                        change="+2.1%"
                        color="green"
                    />
                </div>

                {/* Visitor Analytics Section */}
                {visitorStats && (
                    <div className="mb-8">
                        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <Eye className="w-5 h-5 text-primary-400" />
                            Visitor Analytics
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <div className="glass-card p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                        <Eye className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-white">{visitorStats.today?.pageViews || 0}</p>
                                        <p className="text-xs text-white/50">Page Views Today</p>
                                    </div>
                                </div>
                            </div>
                            <div className="glass-card p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-green-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-white">{visitorStats.today?.uniqueVisitors || 0}</p>
                                        <p className="text-xs text-white/50">Unique Visitors Today</p>
                                    </div>
                                </div>
                            </div>
                            <div className="glass-card p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                        <Eye className="w-5 h-5 text-purple-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-white">{visitorStats.week?.pageViews || 0}</p>
                                        <p className="text-xs text-white/50">Page Views (7d)</p>
                                    </div>
                                </div>
                            </div>
                            <div className="glass-card p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-orange-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-white">{visitorStats.week?.uniqueVisitors || 0}</p>
                                        <p className="text-xs text-white/50">Unique Visitors (7d)</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Traffic Sources & Devices */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Top Pages */}
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-primary-400" />
                                    Top Pages (7d)
                                </h3>
                                <div className="space-y-3">
                                    {visitorStats.topPages?.length > 0 ? (
                                        visitorStats.topPages.map((page, i) => (
                                            <div key={i} className="flex items-center justify-between">
                                                <span className="text-white/70 text-sm truncate max-w-[200px]">{page.path}</span>
                                                <span className="text-white font-medium">{page.views}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-white/40 text-sm">No page data yet</p>
                                    )}
                                </div>
                            </div>

                            {/* Traffic Sources */}
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-accent-400" />
                                    Traffic Sources (7d)
                                </h3>
                                <div className="space-y-3">
                                    {visitorStats.trafficSources?.length > 0 ? (
                                        visitorStats.trafficSources.map((source, i) => (
                                            <div key={i} className="flex items-center justify-between">
                                                <span className="text-white/70 text-sm capitalize">{source.source}</span>
                                                <span className="text-white font-medium">{source.count}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-white/40 text-sm">No traffic data yet</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Device Breakdown */}
                        <div className="glass-card p-6 mt-6">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Monitor className="w-4 h-4 text-ocean-400" />
                                Device Breakdown (7d)
                            </h3>
                            <div className="grid grid-cols-3 gap-4">
                                {visitorStats.devices?.map((device, i) => (
                                    <div key={i} className="text-center">
                                        <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mx-auto mb-2">
                                            {device.type === 'desktop' && <Monitor className="w-6 h-6 text-blue-400" />}
                                            {device.type === 'mobile' && <Smartphone className="w-6 h-6 text-green-400" />}
                                            {device.type === 'tablet' && <Tablet className="w-6 h-6 text-purple-400" />}
                                        </div>
                                        <p className="text-xl font-bold text-white">{device.percentage}%</p>
                                        <p className="text-xs text-white/50 capitalize">{device.type}</p>
                                        <p className="text-xs text-white/30">{device.count} visits</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* User Growth Chart */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">User Growth</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={userGrowth}>
                                    <defs>
                                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                                    <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                                    <Area type="monotone" dataKey="users" stroke="#22d3ee" strokeWidth={2} fill="url(#colorUsers)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Revenue Chart */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Revenue (MRR)</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={userGrowth}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                                    <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} />
                                    <Bar dataKey="revenue" fill="#a855f7" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Bottom Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Users */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Top Users by Activity</h3>
                        <div className="space-y-3">
                            {topUsers.length === 0 ? (
                                <p className="text-white/40 text-sm text-center py-4">No users yet</p>
                            ) : (
                                topUsers.map((user, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white text-sm font-bold">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-white">{user.email}</p>
                                                <p className="text-xs text-white/50">{(user.words || 0).toLocaleString()} words analyzed</p>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${user.isPro ? 'bg-accent-500/20 text-accent-400' : 'bg-white/10 text-white/60'}`}>
                                            {user.isPro ? 'Pro' : 'Free'}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Recent Events */}
                    <div className="glass-card p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">Recent Events</h3>
                        <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                            {recentEvents.length === 0 ? (
                                <p className="text-white/40 text-sm text-center py-4">No recent events</p>
                            ) : (
                                recentEvents.map((event, index) => (
                                    <div key={event.id || index} className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${getEventColor(event.type)}`} />
                                            <div>
                                                <p className="text-sm text-white">{formatEventType(event.type)}</p>
                                                <p className="text-xs text-white/40">{event.email || 'Anonymous'}</p>
                                            </div>
                                        </div>
                                        <span className="text-xs text-white/40">{formatTime(event.timestamp)}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Users List Section */}
                <div className="mt-8">
                    <UsersTable />
                </div>

                {/* Feedback Section */}
                <div className="mt-8">
                    <FeedbackTable />
                </div>
            </main>
        </div>
    )
}

// Metric Card Component
function MetricCard({ icon: Icon, label, value, change, color }) {
    const colorMap = {
        primary: 'bg-primary-500/20 text-primary-400',
        accent: 'bg-accent-500/20 text-accent-400',
        green: 'bg-green-500/20 text-green-400',
        yellow: 'bg-yellow-500/20 text-yellow-400',
        ocean: 'bg-ocean-500/20 text-ocean-400',
        red: 'bg-red-500/20 text-red-400',
    }

    return (
        <div className="glass-card p-5">
            <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${colorMap[color]} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium text-green-400">{change}</span>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{value}</div>
            <div className="text-sm text-white/50">{label}</div>
        </div>
    )
}

// Helper functions
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
}

function formatEventType(type) {
    const map = {
        'text_analyzed': 'Text Analyzed',
        'cliche_detected': 'Cliché Detected',
        'suggestion_used': 'Suggestion Applied',
        'ai_request': 'AI Request',
        'sign_up': 'New Signup',
        'sign_in': 'User Login',
        'upgrade': 'Pro Upgrade',
    }
    return map[type] || type
}

function getEventColor(type) {
    const map = {
        'text_analyzed': 'bg-primary-400',
        'cliche_detected': 'bg-yellow-400',
        'suggestion_used': 'bg-green-400',
        'ai_request': 'bg-accent-400',
        'sign_up': 'bg-blue-400',
        'sign_in': 'bg-white/40',
        'upgrade': 'bg-green-400',
    }
    return map[type] || 'bg-white/40'
}

function formatTime(timestamp) {
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date

    if (diff < 60000) return 'Just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return date.toLocaleDateString()
}

function generateSampleGrowth() {
    const data = []
    const now = new Date()
    for (let i = 29; i >= 0; i--) {
        const date = new Date(now)
        date.setDate(date.getDate() - i)
        data.push({
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            users: 800 + Math.floor(Math.random() * 200) + (30 - i) * 15,
            revenue: 700 + Math.floor(Math.random() * 300) + (30 - i) * 12,
        })
    }
    return data
}

function generateSampleTopUsers() {
    return [
        { email: 'sarah.w@gmail.com', words: 45230, isPro: true },
        { email: 'mike.chen@company.co', words: 38920, isPro: true },
        { email: 'writer.jane@outlook.com', words: 32150, isPro: false },
        { email: 'content.pro@email.com', words: 28440, isPro: true },
        { email: 'alex.writes@mail.com', words: 21890, isPro: false },
    ]
}

function generateSampleEvents() {
    const types = ['text_analyzed', 'cliche_detected', 'suggestion_used', 'ai_request', 'sign_up', 'sign_in', 'upgrade']
    const emails = ['user1@mail.com', 'writer@gmail.com', 'pro@company.co', 'new.user@email.com', null]

    return Array.from({ length: 20 }, (_, i) => ({
        event_type: types[Math.floor(Math.random() * types.length)],
        user_email: emails[Math.floor(Math.random() * emails.length)],
        created_at: new Date(Date.now() - i * 1000 * 60 * Math.floor(Math.random() * 30 + 1)).toISOString(),
    }))
}
